/**
 * Service Model Class
 * Handles the data structure for services in the application
 */
class Service {
  constructor(data = {}) {
    this.id = data.id || null;
    this.name = data.name || '';
    this.description = data.description || '';
    this.price = data.price || 0;
    this.discount = data.discount || 0;
    this.category = data.category || '';
    this.categoryId = data.categoryId || '';
    this.subcategory = data.subcategory || '';
    this.subcategoryId = data.subcategoryId || '';
    this.images = data.images || [];
    this.features = data.features || [];
    this.active = data.active !== undefined ? data.active : true;
    this.popular = data.popular || false;
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
  }

  /**
   * Creates a Service instance from Firestore data
   * @param {string} id - Document ID
   * @param {Object} data - Firestore document data
   * @returns {Service} - Service instance
   */
  static fromFirestore(id, data) {
    return new Service({
      id,
      ...data,
      // Convert Firestore Timestamp to JS Date if needed
      createdAt: data.createdAt ? new Date(data.createdAt.toDate()) : null,
      updatedAt: data.updatedAt ? new Date(data.updatedAt.toDate()) : null
    });
  }

  /**
   * Converts the Service instance to a Firestore-compatible object
   * @returns {Object} - Object ready for Firestore
   */
  toFirestore() {
    // Remove id and null values before saving
    const { id, ...data } = this;
    
    // Create a clean object with non-null values
    const firestoreData = {};
    
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        firestoreData[key] = value;
      }
    });
    
    return firestoreData;
  }

  /**
   * Add a feature to this service
   * @param {string} feature - Feature description
   * @returns {Service} - Updated service instance
   */
  addFeature(feature) {
    if (feature && !this.features.includes(feature)) {
      this.features.push(feature);
    }
    return this;
  }

  /**
   * Remove a feature from this service
   * @param {string} feature - Feature to remove
   * @returns {Service} - Updated service instance
   */
  removeFeature(feature) {
    this.features = this.features.filter(f => f !== feature);
    return this;
  }

  /**
   * Add an image URL to this service
   * @param {string} imageUrl - URL of the image
   * @returns {Service} - Updated service instance
   */
  addImage(imageUrl) {
    if (imageUrl && !this.images.includes(imageUrl)) {
      this.images.push(imageUrl);
    }
    return this;
  }

  /**
   * Remove an image URL from this service
   * @param {string} imageUrl - URL of the image to remove
   * @returns {Service} - Updated service instance
   */
  removeImage(imageUrl) {
    this.images = this.images.filter(img => img !== imageUrl);
    return this;
  }

  /**
   * Format price to currency string
   * @param {string} [locale='en-IN'] - Locale for formatting
   * @param {string} [currency='INR'] - Currency code
   * @returns {string} - Formatted price
   */
  getFormattedPrice(locale = 'en-IN', currency = 'INR') {
    return new Intl.NumberFormat(locale, { 
      style: 'currency', 
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(this.price);
  }

  /**
   * Get the primary image URL or a default placeholder
   * @returns {string} - Image URL
   */
  getPrimaryImage() {
    return this.images && this.images.length > 0 
      ? this.images[0] 
      : '/images/placeholder-service.jpg';
  }

  /**
   * Get a short version of the description
   * @param {number} maxLength - Maximum characters
   * @returns {string} - Truncated description
   */
  getShortDescription(maxLength = 100) {
    if (!this.description || this.description.length <= maxLength) {
      return this.description;
    }
    
    return this.description.substring(0, maxLength) + '...';
  }

  /**
   * Check if the service has multiple images
   * @returns {boolean}
   */
  hasMultipleImages() {
    return this.images && this.images.length > 1;
  }

  /**
   * Get category label with proper formatting
   * @returns {string} - Formatted category name
   */
  getCategoryLabel() {
    return this.category || 'Uncategorized';
  }
}

export default Service; 